/**
 * tween.ts — a minimal ticker-driven tween engine.
 *
 * Pixi's ticker drives `Tweens.update(dtMs)`. Each tween interpolates a number
 * over a duration with an easing function and fires onUpdate/onComplete.
 */
import type { Ease } from './ease';
import { linear } from './ease';

interface TweenInstance {
  elapsed: number;
  duration: number;
  delay: number;
  from: number;
  to: number;
  ease: Ease;
  onUpdate: (v: number) => void;
  onComplete?: () => void;
  done: boolean;
  killed: boolean;
}

export interface TweenHandle {
  kill(): void;
}

class TweenManager {
  private items: TweenInstance[] = [];

  to(opts: {
    from: number;
    to: number;
    duration: number;
    delay?: number;
    ease?: Ease;
    onUpdate: (v: number) => void;
    onComplete?: () => void;
  }): TweenHandle {
    const inst: TweenInstance = {
      elapsed: 0,
      duration: Math.max(1, opts.duration),
      delay: opts.delay ?? 0,
      from: opts.from,
      to: opts.to,
      ease: opts.ease ?? linear,
      onUpdate: opts.onUpdate,
      ...(opts.onComplete ? { onComplete: opts.onComplete } : {}),
      done: false,
      killed: false,
    };
    this.items.push(inst);
    return { kill: () => (inst.killed = true) };
  }

  /** Convenience: run a callback after delayMs. */
  delay(delayMs: number, fn: () => void): TweenHandle {
    return this.to({
      from: 0,
      to: 1,
      duration: 1,
      delay: delayMs,
      onUpdate: () => {},
      onComplete: fn,
    });
  }

  update(dtMs: number): void {
    if (this.items.length === 0) return;
    for (const t of this.items) {
      if (t.killed || t.done) continue;
      if (t.delay > 0) {
        t.delay -= dtMs;
        if (t.delay > 0) continue;
      }
      t.elapsed += dtMs;
      const raw = Math.min(1, t.elapsed / t.duration);
      const v = t.from + (t.to - t.from) * t.ease(raw);
      // A tween whose target display object was destroyed mid-flight (e.g. on scene
      // teardown) must never crash the ticker. Swallow + retire it instead.
      try {
        t.onUpdate(v);
        if (raw >= 1) {
          t.done = true;
          t.onComplete?.();
        }
      } catch {
        t.done = true;
      }
    }
    // compact
    if (this.items.length > 64) {
      this.items = this.items.filter((t) => !t.done && !t.killed);
    } else {
      for (let i = this.items.length - 1; i >= 0; i--) {
        if (this.items[i].done || this.items[i].killed) this.items.splice(i, 1);
      }
    }
  }

  killAll(): void {
    this.items.length = 0;
  }
}

export const Tweens = new TweenManager();
